<template>
  <div class="container mt-5 px-2">
    <article class="message is-danger" v-if="!token">
      <div class="message-body">
        You have not signed in yet. Please go back to <a href="/">home page</a> to sign in first.
      </div>
    </article>
    <div v-if="token">
      <h1 class="title is-4">Vendors</h1>
      <div class="mt-3">
        <div v-if="waiting">
          <span class="icon is-medium is-size-4">
            <i class="fas fa-spinner fa-pulse"></i>
          </span>
        </div>
        <div v-else>
          <table class="table is-fullwidth is-hoverable">
            <thead>
              <tr>
                <th>#</th>
                <th>
                  <span class="is-clickable" @click="changeSortOption('id')">Id</span>
                  <span class="icon" v-if="sortOption.field == 'id'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th class="data-column">
                  <span class="is-clickable" @click="changeSortOption('vendor')">Vendor</span>
                  <span class="icon" v-if="sortOption.field == 'vendor'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th class="data-column">
                  <span class="is-clickable" @click="changeSortOption('subsidiary')">Subsidiary</span>
                  <span class="icon" v-if="sortOption.field == 'subsidiary'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th class="data-column">
                  <span class="is-clickable" @click="changeSortOption('expenseAccount')">Expense account</span>
                  <span class="icon" v-if="sortOption.field == 'expenseAccount'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th class="data-column">
                  <span class="is-clickable" @click="changeSortOption('itemAccount')">Item account</span>
                  <span class="icon" v-if="sortOption.field == 'itemAccount'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th class="data-column">
                  <span class="is-clickable" @click="changeSortOption('itemLocation')">Item location</span>
                  <span class="icon" v-if="sortOption.field == 'itemLocation'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th class="data-column">
                  <span class="is-clickable" @click="changeSortOption('itemClass')">Item class</span>
                  <span class="icon" v-if="sortOption.field == 'itemClass'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th class="data-column">
                  <span class="is-clickable" @click="changeSortOption('reviewer')">Reviewer</span>
                  <span class="icon" v-if="sortOption.field == 'reviewer'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th class="action-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(v, i) in showingVendors" :key="'vendor-' + v.id" class="vendor-row" :class="{'has-background-white-ter': v.id == lastEditing}" :id="'vendor-'+v.id">
                <td>{{i+1}}</td>
                <td>{{v.id}}</td>
                <td class="data-column">
                  <span v-if="!editingItem || editingItem.id != v.id">{{v.vendor}}</span>
                  <div class="field has-addons" v-if="editingItem && editingItem.id == v.id">
                    <div class="control">
                      <input class="input" type="text" v-model="editingItem.vendor">
                    </div>
                    <div class="control">
                      <button class="button is-info" :class="{'is-loading': updating}" @click="updateEditingItem">
                        <i class="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </td>
                <td class="data-column">
                  <span v-if="!editingItem || editingItem.id != v.id">{{v.subsidiary}}</span>
                  <div class="field has-addons" v-if="editingItem && editingItem.id == v.id">
                    <div class="control">
                      <input class="input" type="text" v-model="editingItem.subsidiary">
                    </div>
                    <div class="control">
                      <button class="button is-info" :class="{'is-loading': updating}" @click="updateEditingItem">
                        <i class="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </td>
                <td class="data-column">
                  <span v-if="!editingItem || editingItem.id != v.id">
                    <span v-if="!accountMap[v.expenseAccount]">{{v.expenseAccount}}</span>
                    <abbr v-if="accountMap[v.expenseAccount]" :title="accountMap[v.expenseAccount]">{{v.expenseAccount}}</abbr>
                  </span>
                  <div class="field has-addons" v-if="editingItem && editingItem.id == v.id">
                    <div class="control">
                      <input class="input" type="text" v-model="editingItem.expenseAccount">
                    </div>
                    <div class="control">
                      <button class="button is-info" :class="{'is-loading': updating}" @click="updateEditingItem">
                        <i class="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </td>
                <td class="data-column">
                  <span v-if="!editingItem || editingItem.id != v.id">
                    <span v-for="(a, j) in v.itemAccounts" :key="'v-'+v.id+'-a-'+j">
                      <span v-if="j != 0">,</span>
                      <span>
                        <abbr v-if="accountMap[a]" :title="accountMap[a]">{{a}}</abbr>
                        <span v-else>{{a}}</span>
                      </span>
                    </span>
                  </span>
                  <div class="field has-addons" v-if="editingItem && editingItem.id == v.id">
                    <div class="control">
                      <input class="input" type="text" v-model="editingItem.itemAccount">
                    </div>
                    <div class="control">
                      <button class="button is-info" :class="{'is-loading': updating}" @click="updateEditingItem">
                        <i class="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </td>
                <td class="data-column">
                  <span v-if="!editingItem || editingItem.id != v.id">
                    {{ v.itemLocation }}
                  </span>
                  <div class="field has-addons" v-if="editingItem && editingItem.id == v.id">
                    <div class="control">
                      <input class="input" type="text" v-model="editingItem.itemLocation">
                    </div>
                    <div class="control">
                      <button class="button is-info" :class="{'is-loading': updating}" @click="updateEditingItem">
                        <i class="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </td>
                <td class="data-column">
                  <span v-if="!editingItem || editingItem.id != v.id">
                    {{ v.itemClass }}
                  </span>
                  <div class="field has-addons" v-if="editingItem && editingItem.id == v.id">
                    <div class="control">
                      <input class="input" type="text" v-model="editingItem.itemClass">
                    </div>
                    <div class="control">
                      <button class="button is-info" :class="{'is-loading': updating}" @click="updateEditingItem">
                        <i class="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </td>
                <td class="data-column">
                  <span v-if="!editingItem || editingItem.id != v.id">
                    {{ v.reviewer }}
                  </span>
                  <div class="field has-addons" v-if="editingItem && editingItem.id == v.id">
                    <div class="control">
                      <input class="input" type="text" v-model="editingItem.reviewer">
                    </div>
                    <div class="control">
                      <button class="button is-info" :class="{'is-loading': updating}" @click="updateEditingItem">
                        <i class="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </td>
                <td class="action-column">
                  <span class="vendor-actions">
                    <a @click="setEditingItem(v)">
                      <span class="icon">
                        <i class="fas fa-edit"></i>
                      </span>
                    </a>
                    <a class="has-text-danger ml-3" @click="deleteVendor(v)">
                      <span class="icon">
                        <i class="fas fa-trash"></i>
                      </span>
                    </a>
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>
                  <button class="button" @click="addVendor">
                    <span class="icon">
                      <i class="fas fa-plus"></i>
                    </span>
                  </button>
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div v-if="error" class="notification is-danger is-light">
        <button class="delete" @click="error=''"></button>
        {{error}}
      </div>
    </div>
  </div>
</template>

<script>
import dateFormat from 'dateformat'

export default {
  name: 'Vendors',
  data () {
    return {
      error: '',
      waiting: false,
      vendors: [],
      sortOption: {
        field: 'id',
        asc: true,
      },
      editingItem: null,
      lastEditing: null,
      updating: false,
      accountMap: {},
    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    token () {
      return this.$store.state.user.token
    },
    showingVendors () {
      var sort = this.sortOption
      var sortedVendors = [...this.vendors]
      sortedVendors.sort((a, b) => {
        var va = a[sort.field]
        var vb = b[sort.field]
        if (sort.field == 'id') {
          return sort.asc ? va - vb : vb - va
        }
        if (!va && !vb){
          return 0
        }
        if(!va) {
          return sort.asc ? 1 : -1
        }
        if (!vb) {
          return sort.asc ? -1 : 1
        }
        return sort.asc ? va.localeCompare(vb) : vb.localeCompare(va)
      })
      return sortedVendors
    },
  },
  methods: {
    getVendors () {
      this.waiting = true
      this.$http.get(this.server + '/myapp/get-vendor-subsidiaries/').then(resp => {
        var vendors = resp.body.map(v => ({
          ...v,
          itemAccounts: v.itemAccount.split(',').map(s => s.trim()).filter(s => Boolean(s)),
        }))
        this.vendors = vendors
        this.waiting = false
      }, err => {
        this.error = err.body
        this.waiting = false
      })
    },
    changeSortOption (field) {
      if (this.sortOption.field == field) {
        this.sortOption.asc = !this.sortOption.asc
        return
      }
      this.sortOption.field = field
    },
    setEditingItem (vendor) {
      if (this.editingItem && this.editingItem.id == vendor.id) {
        this.editingItem = null
        this.lastEditing = null
        return
      }
      this.editingItem = {...vendor}
      this.lastEditing = vendor.id
    },
    updateEditingItem () {
      this.updating = true
      this.$http.post(this.server + '/myapp/update-vendor-subsidiary/', this.editingItem).then(resp => {
        this.updating = false
        this.updateVendorFromUpdating()
        this.scrollToVendor(this.editingItem.id)
        this.editingItem = null
      }, err => {
        this.error = err.body
        this.updating = false
      })
    },
    deleteVendor (vendor) {
      var confirm = {
        title: 'Delete vendor',
        message: 'Are you sure to remove this vendor: ' + JSON.stringify(vendor) + '?',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.deleteVendorConfirmed,
          args: [vendor]
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    deleteVendorConfirmed (vendor) {
      this.$http.post(this.server + '/myapp/delete-vendor-subsidiary/', {id: vendor.id}).then(resp => {
        var index = this.vendors.findIndex(v => v.id == vendor.id)
        this.vendors.splice(index, 1)
      }, err => {
        this.error = err.body
      })
    },
    addVendor () {
      var confirm = {
        title: 'Add vendor',
        message: 'Are you sure to add an empty vendor?',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.addVendorConfirmed,
          args: []
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    addVendorConfirmed () {
      var message = {vendor: '', subsidiary: '', accounts: '', expenseAccount: '', itemAccount: '', itemLocation: '', itemClass: '', reviewer: ''}
      this.$http.post(this.server + '/myapp/create-vendor-subsidiary/', message).then(resp => {
        var vendor = resp.body.map(v => ({
          ...v,
          itemAccounts: v.itemAccount.split(',').map(s => s.trim()).filter(s => Boolean(s)),
        }))
        this.vendors.push(vendor)
        this.scrollToVendor(vendor.id)
      }, err => {
        this.error = err.body
      })
    },
    updateVendorFromUpdating() {
      var index = this.vendors.findIndex(v => v.id == this.editingItem.id)
      this.vendors.splice(index, 1, this.editingItem)
    },
    scrollToVendor(vendorId) {
      console.log('scrollToVendor', vendorId)
      setTimeout(() => {
        const targetElement = document.getElementById("vendor-" + vendorId);
        if (targetElement) {
          targetElement.scrollIntoView({ 
            behavior: "smooth", // "auto" for instant jump
            block: "start",     // Vertically aligns to "start", "center", "end", or "nearest"
            inline: "nearest"   // Horizontally aligns the element
          });
        }
      }, 1000)
    },
    getAccounts () {
      this.$http.get(this.server + '/myapp/get-accounts/').then(resp => {
        var accountMap = {}
        resp.body.forEach(a => {
          accountMap[a.nsId] = a.name
        })
        this.accountMap = accountMap
      }, err => {
        this.error = err.body
      })
    },
  },
  mounted () {
    this.$nextTick(function () {
      this.getVendors()
      this.getAccounts()
    })
  },
}
</script>

<style scoped lang="scss">
.vendor-row .vendor-actions {
  display: none;
}

.vendor-row:hover .vendor-actions {
  display: block;
}

.action-column {
  min-width: 100px;
}

.data-column {
  min-width: 150px;
}
</style>
